import { getDocumentPanelPath, getDocumentPath } from './document-panel-paths';

export const COMMENT_NOTIFICATION_REPLY_CTA = 'Reply at the document';

export interface NotificationTarget {
  href: string;
  external: boolean;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function toInAppHref(urlOrPath: string): string {
  if (urlOrPath.startsWith('/')) {
    return urlOrPath;
  }
  try {
    const parsed = new URL(urlOrPath);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return urlOrPath;
  }
}

function buildCommentPath(metadata: Record<string, unknown>): string | null {
  const documentId = asString(metadata.documentId);
  if (!documentId) {
    return null;
  }
  const projectId = asString(metadata.projectId) ?? undefined;
  const commentId = asString(metadata.commentId);
  const replyId = asString(metadata.replyId);
  const base = getDocumentPath(projectId, documentId);
  if (!commentId) {
    return getDocumentPanelPath(projectId, documentId, 'comments');
  }
  const params = new URLSearchParams({ commentId, focusReply: '1' });
  if (replyId) {
    params.set('replyId', replyId);
  }
  return `${base}?${params.toString()}`;
}

function buildDocumentPath(metadata: Record<string, unknown>): string | null {
  const documentUrl = asString(metadata.documentUrl);
  if (documentUrl) {
    return toInAppHref(documentUrl);
  }
  const documentId = asString(metadata.documentId);
  if (!documentId) {
    return null;
  }
  const projectId = asString(metadata.projectId) ?? undefined;
  return getDocumentPath(projectId, documentId);
}

function buildProjectPath(metadata: Record<string, unknown>): string | null {
  const projectUrl = asString(metadata.projectUrl);
  if (projectUrl) {
    return toInAppHref(projectUrl);
  }
  const projectId = asString(metadata.projectId);
  if (!projectId) {
    return null;
  }
  return `/projects/${projectId}`;
}

export function getNotificationDocumentTarget(
  type: string,
  metadata: Record<string, unknown> | null
): NotificationTarget | null {
  if (!metadata) {
    return null;
  }
  if (
    type !== 'comment_created' &&
    type !== 'comment_reply_created' &&
    type !== 'user_mentioned'
  ) {
    return null;
  }
  const href = buildDocumentPath(metadata);
  return href ? { href, external: false } : null;
}

export function getNotificationTarget(
  type: string,
  metadata: Record<string, unknown> | null,
  options?: { isAdmin?: boolean }
): NotificationTarget | null {
  if (!metadata) {
    return null;
  }

  if (
    type === 'comment_created' ||
    type === 'comment_reply_created' ||
    type === 'user_mentioned'
  ) {
    const href = buildCommentPath(metadata);
    return href ? { href, external: false } : null;
  }

  if (type === 'project_shared' || type === 'project_share_pending') {
    const href = buildProjectPath(metadata);
    return href ? { href, external: false } : null;
  }

  if (type === 'user_feedback_submitted') {
    if (!options?.isAdmin) {
      return null;
    }
    const feedbackId = asString(metadata.feedbackId);
    if (!feedbackId) {
      return null;
    }
    return {
      href: `/admin/user-feedback?feedbackId=${encodeURIComponent(feedbackId)}`,
      external: false,
    };
  }

  if (type === 'feedback_github_link_added' || type === 'feedback_issue_closed') {
    const githubIssueUrl = asString(metadata.githubIssueUrl);
    if (!githubIssueUrl) {
      return null;
    }
    return { href: githubIssueUrl, external: true };
  }

  if (
    type === 'document_shared' ||
    type === 'document_share_pending' ||
    type === 'feedback_marked_done'
  ) {
    if (type === 'feedback_marked_done') {
      const githubIssueUrl = asString(metadata.githubIssueUrl);
      if (githubIssueUrl) {
        return { href: githubIssueUrl, external: true };
      }
      return null;
    }
    const href = buildDocumentPath(metadata);
    return href ? { href, external: false } : null;
  }

  const documentHref = buildDocumentPath(metadata);
  if (documentHref) {
    return { href: documentHref, external: false };
  }

  const projectHref = buildProjectPath(metadata);
  if (projectHref) {
    return { href: projectHref, external: false };
  }

  return null;
}
